import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { TablerIconsModule } from 'angular-tabler-icons';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { FileUploadService } from '../../services/apps/file/file-upload.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-media-viewer',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTabsModule,
    TablerIconsModule
  ],
  templateUrl: './media-viewer.component.html',
  styleUrls: ['./media-viewer.component.scss']
})
export class MediaViewerComponent implements OnInit, OnDestroy {
  fileUrl: string = '';
  fileName: string = '';
  fileExtension: string = '';
  fileType: 'text' | 'image' | 'video' | 'pdf' | 'excel' | 'word' | 'unsupported' = 'unsupported';
  safeUrl: SafeResourceUrl | null = null;
  blobUrl: string | null = null; // Blob URL'lerini temizlemek için
  fileContent: string = '';
  isLoading: boolean = false;
  error: string = '';

  // Download için gerekli bilgiler
  organizationId: number | null = null;
  accessToken: string | null = null;

  excelData: any[] = [];
  excelSheets: string[] = [];
  selectedSheetIndex: number = 0;
  excelColumns: string[] = [];

  // Image zoom properties
  imageZoom: number = 1;
  imagePanX: number = 0;
  imagePanY: number = 0;
  isDragging: boolean = false;
  lastMouseX: number = 0;
  lastMouseY: number = 0;
  imageLoaded: boolean = false;

  supportedExtensions = ['.txt', '.md', '.pdf', '.doc', '.docx', '.odt', '.xlsx', '.xls', '.ods', '.csv', '.png', '.jpg', '.jpeg', '.mp4'];

  private excelWorkbook: any = null;

  @ViewChild('imageElement', { static: false }) imageElement!: ElementRef<HTMLImageElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private toastrService: ToastrService,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    // Query parametrelerinden dosya bilgilerini al
    this.route.queryParams.subscribe(params => {
      if (params['fileUrl']) {
        this.fileUrl = params['fileUrl'];
        this.fileName = params['fileName'] || 'Bilinmeyen Dosya';
        // Download için gerekli bilgileri al
        this.organizationId = params['organizationId'] ? parseInt(params['organizationId']) : null;
        this.accessToken = params['accessToken'] || null;
        this.loadFile();
      } else {
        this.openFileDialog();
      }
    });
    // Sağ tık menüsünü global olarak engelle (sadece PDF için)
    this.addContextMenuPrevention();
  }

  ngOnDestroy(): void {
    // Cleanup
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
    // Event listener'ları temizle
    this.removeContextMenuPrevention();
  }

  private addContextMenuPrevention(): void {
    // PDF görüntülenirken sağ tık menüsünü engelle
    document.addEventListener('contextmenu', this.preventContextMenu.bind(this));
    document.addEventListener('selectstart', this.preventSelection.bind(this));
    document.addEventListener('dragstart', this.preventDragStart.bind(this));
  }

  private removeContextMenuPrevention(): void {
    document.removeEventListener('contextmenu', this.preventContextMenu.bind(this));
    document.removeEventListener('selectstart', this.preventSelection.bind(this));
    document.removeEventListener('dragstart', this.preventDragStart.bind(this));
  }

  private preventContextMenu(event: MouseEvent): void {
    // Sadece PDF görüntülenirken ve iframe içindeyse engelle
    if (this.fileType === 'pdf') {
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'IFRAME' || target.closest('iframe'))) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  private preventSelection(event: Event): void {
    // PDF iframe'inde metin seçimini engelle (opsiyonel)
    if (this.fileType === 'pdf') {
      const target = event.target as HTMLElement;
      if (target && target.tagName === 'IFRAME') {
        event.preventDefault();
      }
    }
  }

  private preventDragStart(event: DragEvent): void {
    // PDF iframe'inde drag&drop'u engelle
    if (this.fileType === 'pdf') {
      const target = event.target as HTMLElement;
      if (target && target.tagName === 'IFRAME') {
        event.preventDefault();
      }
    }
  }

  openFileDialog(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = this.supportedExtensions.join(',');

    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.handleFileSelection(file);
      } else {
        this.goBack();
      }
    };

    input.click();
  }

  handleFileSelection(file: File): void {
    // Önceki blob URL'ini temizle
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }

    this.fileName = file.name;
    this.fileExtension = this.getFileExtension(file.name);

    if (!this.supportedExtensions.includes(this.fileExtension)) {
      this.error = `Bu dosya türü (${this.fileExtension}) desteklenmiyor. Lütfen desteklenen bir dosya formatı seçin.`;
      return;
    }

    this.isLoading = true;
    this.error = '';
    // Dosya türünü belirle
    this.determineFileType();
    // Dosyayı yükle
    this.loadFileFromBlob(file);
  }

  loadFile(): void {
    // Önceki blob URL'ini temizle
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }

    this.fileExtension = this.getFileExtension(this.fileName);
    this.determineFileType();
    if (this.fileType === 'excel') {
      // Excel dosyası için URL'den yükle
      this.loadExcelFromUrl();
    } else if (this.fileUrl) {
      let urlToUse = this.fileUrl;

      if (this.fileUrl.startsWith('token:')){

      }// URL formatını kontrol et
      if (this.fileUrl.startsWith('data:') || this.fileUrl.startsWith('blob:') || this.fileUrl.startsWith('http')) {
        // Text dosyaları için içeriği oku
        if (this.fileType === 'text') {
          fetch(this.fileUrl)
            .then(response => response.text())
            .then(text => {
              this.fileContent = text;
              this.isLoading = false;
            })
            .catch(error => {
              console.error('Text içeriği okuma hatası:', error);
              this.error = 'Dosya içeriği okunamadı.';
              this.isLoading = false;
            });
        } else if (this.fileType === 'pdf') {
          // PDF dosyaları için toolbar'ı gizle
          urlToUse = this.fileUrl + '#toolbar=0&navpanes=0&scrollbar=0';

          try {
            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlToUse);
            this.isLoading = false;
          } catch (error) {
            console.error('Safe URL oluşturma hatası:', error);
            this.error = 'Dosya URL\'i güvenli bir şekilde işlenemedi.';
            this.isLoading = false;
          }
        } else {
          // Diğer dosya türleri için (resim, video)
          try {
            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlToUse);
            this.isLoading = false;

            // Resim dosyaları için imageLoaded durumunu kontrol et
            if (this.fileType === 'image') {
              // Resim yükleme durumunu sıfırla
              this.imageLoaded = false;
              this.error = '';

              // Resim yükleme timeout'u ekle
              setTimeout(() => {
                if (!this.imageLoaded) {
                  console.warn('Resim yükleme timeout - manuel olarak yüklendi olarak işaretleniyor');
                  this.imageLoaded = true;
                }
              }, 5000); // 5 saniye sonra timeout
            }
          } catch (error) {
            console.error('Safe URL oluşturma hatası:', error);
            this.error = 'Dosya URL\'i güvenli bir şekilde işlenemedi.';
            this.isLoading = false;
          }
        }
      } else {
        this.error = 'Geçersiz dosya URL formatı.';
        this.isLoading = false;
      }
    } else {
      this.error = 'Dosya URL\'i bulunamadı.';
      this.isLoading = false;
    }
  }

  loadFileFromBlob(file: File): void {
    const reader = new FileReader();

    if (this.fileType === 'text') {
      reader.onload = (e) => {
        this.fileContent = e.target?.result as string;
        this.isLoading = false;
      };
      reader.readAsText(file);
    } else if (this.fileType === 'excel') {
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        this.processExcelData(workbook);
      };
      reader.readAsArrayBuffer(file);
    } else if (this.fileType === 'pdf') {
      // PDF dosyaları için doğru MIME type ile blob oluştur
      reader.onload = (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const urlToUse = url + '#toolbar=0&navpanes=0&scrollbar=0';

        this.blobUrl = url; // Blob URL'ini sakla
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlToUse);
        this.isLoading = false;
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Diğer dosya türleri için (resim, video)
      const url = URL.createObjectURL(file);
      this.blobUrl = url; // Blob URL'ini sakla
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.isLoading = false;

      // Resim dosyaları için imageLoaded durumunu kontrol et
      if (this.fileType === 'image') {
        this.imageLoaded = false;
        this.error = '';

        // Resim yükleme timeout'u ekle
        setTimeout(() => {
          if (!this.imageLoaded) {
            console.warn('Resim yükleme timeout - manuel olarak yüklendi olarak işaretleniyor');
            this.imageLoaded = true;
          }
        }, 5000); // 5 saniye sonra timeout
      }
    }
  }

  loadExcelFromUrl(): void {
    this.isLoading = true;

    if (this.fileUrl.startsWith('data:')) {
      // Base64 encoded veri
      try {
        // Data URL'den base64 kısmını çıkar
        const base64Data = this.fileUrl.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const workbook = XLSX.read(bytes, { type: 'array' });
        this.processExcelData(workbook);
      } catch (error) {
        this.error = 'Excel dosyası işlenirken bir hata oluştu.';
        this.isLoading = false;
      }
    } else {
      // HTTP URL'den fetch
      fetch(this.fileUrl)
        .then(response => response.arrayBuffer())
        .then(data => {
          const uint8Array = new Uint8Array(data);
          const workbook = XLSX.read(uint8Array, { type: 'array' });
          this.processExcelData(workbook);
        })
        .catch(error => {
          console.error('Excel dosyası yüklenirken hata:', error);
          this.error = 'Excel dosyası yüklenirken bir hata oluştu.';
          this.isLoading = false;
        });
    }
  }

  processExcelData(workbook: XLSX.WorkBook): void {
    try {
      // Workbook'u sakla
      this.excelWorkbook = workbook;

      // Sheet isimlerini al
      this.excelSheets = workbook.SheetNames;

      if (this.excelSheets.length === 0) {
        this.error = 'Excel dosyasında sheet bulunamadı.';
        this.isLoading = false;
        return;
      }

      // İlk sheet'i yükle
      this.loadExcelSheet(0);

    } catch (error) {
      this.error = 'Excel dosyası işlenirken bir hata oluştu.';
      this.isLoading = false;
    }
  }

  loadExcelSheet(sheetIndex: number): void {
    if (!this.excelSheets[sheetIndex] || !this.excelWorkbook) {
      console.error('Sheet veya workbook bulunamadı:', { sheetIndex, sheetsLength: this.excelSheets?.length, hasWorkbook: !!this.excelWorkbook });
      this.isLoading = false;
      return;
    }

    // Aynı sheet zaten yüklüyse tekrar yükleme
    if (this.selectedSheetIndex === sheetIndex && this.excelData.length > 0) {
      this.isLoading = false;
      return;
    }

    // Loading state'i aktif et
    this.isLoading = true;
    this.error = '';

    // Basit timeout ile UI güncelleme
    setTimeout(() => {
      try {
        this.processSheetSync(sheetIndex);
      } catch (error) {
        console.error(`Sheet "${this.excelSheets[sheetIndex]}" işlenirken hata:`, error);
        this.error = `Sheet "${this.excelSheets[sheetIndex]}" işlenirken bir hata oluştu.`;
        this.isLoading = false;
      }
    }, 10);
  }

  private processSheetSync(sheetIndex: number): void {
    const worksheet = this.excelWorkbook.Sheets[this.excelSheets[sheetIndex]];
    // Sheet'in range'ini kontrol et
    if (!worksheet['!ref']) {
      this.excelColumns = [];
      this.excelData = [];
      this.selectedSheetIndex = sheetIndex;
      this.isLoading = false;
      return;
    }
    // Küçük dosyalar için basit işleme
    try {
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        raw: false
      });

      if (jsonData.length === 0) {
        this.excelColumns = [];
        this.excelData = [];
        this.selectedSheetIndex = sheetIndex;
        this.isLoading = false;
        return;
      }
      // İlk satırı başlık olarak kullan
      const headerRow = jsonData[0] as any[];
      // Kolon başlıklarını oluştur
      this.excelColumns = headerRow.map((col, index) => {
        if (!col || col.toString().trim() === '') {
          return this.getColumnLetter(index);
        }
        return col.toString();
      });
      // Veri satırlarını işle
      this.excelData = jsonData.slice(1).map((row: any[]) => {
        const rowObj: any = {};
        this.excelColumns.forEach((columnName, index) => {
          rowObj[columnName] = row[index] || '';
        });
        return rowObj;
      });

      this.selectedSheetIndex = sheetIndex;
      this.isLoading = false;

    } catch (error) {
      console.error('Sheet işlenirken hata:', error);
      this.error = 'Sheet işlenirken bir hata oluştu.';
      this.isLoading = false;
    }
  }

  // Kolon harfini döndüren yardımcı fonksiyon (A, B, C, ..., AA, AB, ...)
  getColumnLetter(index: number): string {
    let result = '';
    while (index >= 0) {
      result = String.fromCharCode(65 + (index % 26)) + result;
      index = Math.floor(index / 26) - 1;
    }
    return result;
  }

  onSheetChange(event: any): void {
    const newSheetIndex = event.index;
    // Aynı sheet seçildiyse işlem yapma
    if (newSheetIndex === this.selectedSheetIndex) {
      return;
    }
    // Önceki sheet'in verilerini temizle
    this.excelColumns = [];
    this.excelData = [];
    // Direkt olarak yeni sheet'i yükle
    this.loadExcelSheet(newSheetIndex);
  }

  loadLocalFile(): void {
    // Local dosya yükleme implementasyonu
    this.isLoading = false;
  }

  determineFileType(): void {
    const ext = this.fileExtension.toLowerCase();

    if (['.txt', '.md'].includes(ext)) {
      this.fileType = 'text';
    } else if (['.png', '.jpg', '.jpeg'].includes(ext)) {
      this.fileType = 'image';
      this.imageLoaded = false; // Resim türü değiştiğinde yükleme durumunu sıfırla
    } else if (['.mp4'].includes(ext)) {
      this.fileType = 'video';
    } else if (ext === '.pdf' || ['.doc', '.docx', '.odt'].includes(ext)) {
      this.fileType = 'pdf';
    } else if (['.xlsx', '.xls', '.ods', '.csv'].includes(ext)) {
      this.fileType = 'excel';
    } else {
      this.fileType = 'unsupported';
    }
  }

  getFileExtension(fileName: string): string {
    return fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  }

  goBack(): void {
    window.close(); // Yeni sekme ise kapat
    // Eğer kapatılamıyorsa, önceki sayfaya dön
    setTimeout(() => {
      this.router.navigate(['/apps/resource-management']);
    }, 100);
  }

  downloadFile(): void {
    // Backend'den indirme için gerekli bilgileri kontrol et
    if (!this.organizationId || !this.accessToken) {
      this.toastrService.error('Dosya indirme bilgileri eksik. Lütfen sayfayı yenileyin.');
      return;
    }

    // İndirme başladığını bildir
    this.toastrService.info('Dosyanız indiriliyor...', 'İndirme', {
      timeOut: 3000,
      progressBar: true
    });

    // Backend'den dosyayı indir
    this.fileUploadService.downloadFile(this.organizationId, this.accessToken).subscribe({
      next: (response: any) => {
        try {
          // Response'ı blob olarak işle
          let blob: Blob;

          if (response instanceof Blob) {
            blob = response;
          } else {
            // String response ise blob'a çevir
            blob = new Blob([response], { type: 'application/octet-stream' });
          }
          // Dosyayı indir
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = this.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          // Başarı mesajı göster
          this.toastrService.success('Başarılı bir şekilde dosyanız indirildi', 'İndirme Tamamlandı', {
            timeOut: 5000,
            progressBar: true
          });
        } catch (error) {
          console.error('Dosya indirme işlemi sırasında hata:', error);
          this.toastrService.error('Dosya indirme işlemi sırasında bir hata oluştu.');
        }
      },
      error: (error) => {
        console.error('Download error:', error);
        this.toastrService.error('Dosya indirilemedi. Lütfen tekrar deneyin.', 'İndirme Hatası');
      }
    });
  }

  // Image zoom and pan methods
  onImageWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.5, Math.min(5, this.imageZoom + delta));

    // Zoom to mouse position
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    this.imageZoom = newZoom;
    this.updateImageTransform();
  }

  onImageMouseDown(event: MouseEvent): void {
    if (this.imageZoom > 1) {
      this.isDragging = true;
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      event.preventDefault();
    }
  }

  onImageMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const deltaX = event.clientX - this.lastMouseX;
      const deltaY = event.clientY - this.lastMouseY;

      this.imagePanX += deltaX / this.imageZoom;
      this.imagePanY += deltaY / this.imageZoom;

      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;

      this.updateImageTransform();
      event.preventDefault();
    }
  }

  onImageMouseUp(event: MouseEvent): void {
    this.isDragging = false;
  }

  zoomIn(): void {
    this.imageZoom = Math.min(5, this.imageZoom + 0.25);
    this.updateImageTransform();
  }

  zoomOut(): void {
    this.imageZoom = Math.max(0.5, this.imageZoom - 0.25);
    this.updateImageTransform();
  }

  resetZoom(): void {
    this.imageZoom = 1;
    this.imagePanX = 0;
    this.imagePanY = 0;
    this.isDragging = false;
  }

  private updateImageTransform(): void {
    // Bu metod şu anda boş, transform HTML'de yapılıyor
    // Gelecekte ek işlemler gerekirse buraya eklenebilir
  }

  onImageLoad(): void {
    this.imageLoaded = true;
    this.resetZoom(); // Resim yüklendiğinde zoom'u sıfırla

    // Resim boyutlarını kontrol et
    if (this.imageElement && this.imageElement.nativeElement) {
      const img = this.imageElement.nativeElement;
    }
  }

  onImageError(): void {
    console.error('Resim yükleme hatası:', this.fileName);
    this.imageLoaded = false;
    this.error = 'Resim yüklenirken bir hata oluştu.';
  }

  // Dosya açma fonksiyonu - diğer componentlerden çağırılabilir
  static openFile(fileUrl?: string, fileName?: string): void {
    const url = `/media-viewer?fileUrl=${encodeURIComponent(fileUrl || '')}&fileName=${encodeURIComponent(fileName || '')}`;
    window.open(url, '_blank', '');
  }
}
